import React, {
  FunctionComponent,
  KeyboardEventHandler,
  useCallback,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'record-it-utils/utils/Key/Key';
import {
  CardHeader,
  HorizontalAlignment
} from '../../card/CardHeader/CardHeader';
import {
  CardStandard,
  CardStandardProps
} from '../../card/CardStandard/CardStandard';
import {
  ButtonStandard,
  ButtonStandardHoverEffectType,
  ButtonStandardType
} from '../../button/ButtonStandard/ButtonStandard';
import { FormGroup } from '../FormGroup/FormGroup';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { prepareProps } from '../../utils/propUtils';
import { CardBody } from '../../card/CardBody/CardBody';
import { TextInput, TextInputType } from '../../input/TextInput/TextInput';
import { useRefWithCurrentValue } from '../../hooks/useRefWithCurrentValue';
import { useEventCallback } from '../../hooks/useEventCallback';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import styles from './LoginForm.module.css';

export type LoginFormProps = PropsWithoutChildren<CardStandardProps> & {
  login: LoginFunction;
  onForgotPassword: (email: string) => void;
  onCreateAccount: () => void;
  errorMessage?: string;
};

export const LoginForm: FunctionComponent<LoginFormProps> = React.memo(
  (props) => {
    const {
      login,
      onCreateAccount,
      onForgotPassword,
      errorMessage,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const isLoggingInRef = useRefWithCurrentValue(isLoggingIn);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const { t } = useTranslation('record-it-components');

    const handleLoginClick = useEventCallback((): void => {
      if (isLoggingInRef.current) {
        return;
      }

      setIsLoggingIn(true);
      void login(email, password).finally(() => {
        setIsLoggingIn(false);
      });
    });

    const handlePasswordKeyDown: KeyboardEventHandler = useCallback(
      (event) => {
        if (event.key === Key.Enter) {
          handleLoginClick();
        }
      },
      [handleLoginClick]
    );

    const handleForgotPasswordClick = useEventCallback(() => {
      onForgotPassword(email);
    });

    return (
      <CardStandard
        {...remainingProps}
        className={styles.card}
      >
        <CardHeader textAlignment={HorizontalAlignment.Center}>
          {t('form.LoginForm.title')}
        </CardHeader>
        <CardBody>
          {errorMessage ? (
            <MessageBox
              type={MessageBoxType.Error}
              className={styles.messageBox}
              data-testid="LoginForm.errorMessageBox"
            >
              {errorMessage}
            </MessageBox>
          ) : null}
          <FormGroup>
            <TextInput
              label={t('form.LoginForm.emailLabel')}
              type={TextInputType.Email}
              value={email}
              onInput={setEmail}
              data-testid="LoginForm.emailTextInput"
            ></TextInput>
          </FormGroup>
          <FormGroup>
            <TextInput
              label={t('form.LoginForm.passwordLabel')}
              type={TextInputType.Password}
              value={password}
              onInput={setPassword}
              onKeyDown={handlePasswordKeyDown}
              data-testid="LoginForm.passwordTextInput"
            ></TextInput>
          </FormGroup>
          <div className={styles.forgotPasswordButtonWrapper}>
            <ButtonStandard
              type={ButtonStandardType.TextSecondary}
              hoverEffectType={ButtonStandardHoverEffectType.HalfTransparent}
              onClick={handleForgotPasswordClick}
              data-testid="LoginForm.forgotPasswordButton"
            >
              {t('form.LoginForm.forgotPassword')}
            </ButtonStandard>
          </div>
          <div className={styles.loginButtonWrapper}>
            <ButtonStandard
              type={ButtonStandardType.Primary}
              onClick={handleLoginClick}
              data-testid="LoginForm.loginButton"
            >
              {t('form.LoginForm.loginButtonLabel')}
            </ButtonStandard>
          </div>
          {/* <div className={styles.createAccountButtonWrapper}>
          <ButtonStandard
            type={ButtonStandardType.TextSecondary}
            hoverEffectType={ButtonStandardHoverEffectType.HalfTransparent}
            onClick={onCreateAccount}
            data-testid="LoginForm.createAccountButton"
          >
            {t('form.LoginForm.createAccount')}
          </ButtonStandard>
        </div> */}
          <LoadingOverlay active={isLoggingIn}></LoadingOverlay>
        </CardBody>
      </CardStandard>
    );
  }
);

export type LoginFormI18N = {
  /** e.g. Login */
  title: string;
  /** e.g. E-Mail */
  emailLabel: string;
  /** e.g. Passwort */
  passwordLabel: string;
  /** e.g. Login */
  loginButtonLabel: string;
  /** e.g. Account erstellen */
  createAccount: string;
  /** e.g. Passwort vergessen */
  forgotPassword: string;
};

export type LoginFunction = (email: string, password: string) => Promise<void>;
